import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Avatar } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';

import { createTanstackTable, OrganizationRolesOptions } from '../common/utils';
import { useGetProjects } from '../hooks';
import {
  useGetOrganizations,
  useGetOrgInvitations,
  useGetOrgMembers,
} from '../hooks/tanstack/useOrganizationHook';
import { useGetUser } from '../hooks/tanstack/useUserHook';
import {
  isOrgAdmin,
  useGetRouteParams,
  useGetSearchResults,
} from '../hooks/tanstack/useUtilsHook';
import {
  type OrganizationRole,
  type OrganizationStatus,
  type UserStatus,
} from '../types';
import cn from '../utils/cn';
import { useCurrentMembership, useCurrentOrganization } from '../utils/helpers';

import FormSelect from './form/FormSelect';
import CustomMenuDropdown from './model/CustomMenuDropdown';
import InviteOrgFormDialog from './organizations/InviteOrgFormDialog';
import ConfirmDeleteUserDialog from './userManagement/ConfirmDeleteUserDialog';
import ResendOrgInvitation from './userManagement/ResendOrgInvitation';
import SearchInput from './SearchInput';
import TanstackTable from './TanstackTable';

export type DataRow = {
  id: number;
  user: {
    id: number;
    name?: string | null;
    avatarImage: string;
    email: string;
    role: OrganizationRole;
  };
  projects:
    | {
        id: number;
        name: string;
      }[]
    | null;
  type: OrganizationStatus;
  lastActive: string | null;
  status: UserStatus;
};

const roles = [
  {
    value: 'organization_member',
    label: 'Organization Member',
  },
  {
    value: 'organization_admin',
    label: 'Organization Admin',
  },
  {
    value: 'organization_owner',
    label: 'Organization Owner',
  },
] as {
  value: OrganizationRole;
  label: string;
}[];

const columnHelper = createColumnHelper<DataRow>();

const columns = ({ handleSelect }: { handleSelect: (id: number) => void }) => [
  columnHelper.accessor('user', {
    cell: (info) => {
      const { email, role, name, avatarImage } = info.getValue();

      return (
        <div className="group flex items-center gap-1 text-start">
          <div className="flex items-center gap-2">
            <Avatar
              alt="User avatar"
              src={avatarImage}
              sx={{
                height: 40,
                width: 40,
                rounded: 'full',
              }}
            />
            <div className="flex flex-col">
              <div className="flex w-full justify-between gap-3">
                <p
                  className={cn('font-semibold', {
                    'opacity-70': !name,
                  })}
                >
                  {name && name.length > 0 ? name : email?.split('@')?.[0]}
                </p>
                <p
                  className={cn(
                    'flex items-center rounded-full px-3 text-xs font-bold uppercase',
                    {
                      'bg-[#FFE8B9]': role === 'organization_admin',
                      'bg-[#E8F2FF]': role === 'organization_owner',
                      'bg-[#F5F5F5]': role === 'organization_member',
                    }
                  )}
                >
                  {
                    OrganizationRolesOptions.find((r) => r.value === role)
                      ?.label
                  }
                </p>
              </div>
              <p className="text-sm text-[#666]">{email}</p>
            </div>
          </div>
        </div>
      );
    },
    header: () => (
      <div className="flex items-center gap-1">
        <p>User</p>
      </div>
    ),
  }),
  columnHelper.accessor('lastActive', {
    id: 'lastActive',
    cell: (info) => {
      const id = info.row.original.id;

      return info.getValue() ? (
        <div className="w-full">
          {format(info.getValue() as string, 'y/MM/dd h:mm a')}
        </div>
      ) : (
        <div className="text-gray-400 w-full text-start">--</div>
      );
    },
    sortingFn: (a, b) =>
      new Date(a.original.lastActive as string).getTime() -
      new Date(b.original.lastActive as string).getTime(),
    header: () => <div>Last active</div>,
  }),
  columnHelper.accessor('status', {
    id: 'status',
    cell: (info) => {
      const status = info.getValue();
      const id = info.row.original.id;

      const { orgId } = useGetRouteParams();

      const membership = useCurrentMembership();

      const { data: org_invitations } = useGetOrgInvitations(
        { orgId },
        {
          enabled: !!orgId && isOrgAdmin(membership.organization?.role),
        }
      );

      const { data: org_members } = useGetOrgMembers(
        { orgId },
        {
          enabled: !!orgId && isOrgAdmin(membership.organization?.role),
        }
      );

      const { data: currentUser } = useGetUser();

      const { data: organizations } = useGetOrganizations();

      const row = info.row.original;

      const user = {
        ...row,
        email: row.user.email,
        role: row.user.role,
        type:
          status === 'pending'
            ? 'invitation'
            : ('member' as 'invitation' | 'member'),
      };

      const member = org_members?.find((m) => m.user.id === user.id);
      const orgMember = organizations?.find((o) => o.id === orgId);

      const invitation = org_invitations?.find(
        (i: { email: string }) => i.email === user.email
      );

      if (member) {
        user['id'] = member.id;
      } else if (invitation) {
        user['id'] = invitation.id;
      }

      const StatusLabel = ({
        label,
        value,
      }: {
        value: 'active' | 'pending';
        label: string;
      }) => (
        <div className="flex justify-start">
          <p
            className={cn(
              'w-fit rounded-full bg-[#E1FBE7] px-6 text-green-600',
              {
                'bg-[#EEF6FF] text-[#117DD3]': value === 'active',
                'text-[#C38D04] bg-[#FFF8E0]': value === 'pending',
              }
            )}
          >
            {label}
          </p>
        </div>
      );

      return (
        <div className="flex items-center justify-between">
          <div
            className="w-full"
            onClick={() => {
              handleSelect(id);
            }}
          >
            {status === 'active' ? (
              <StatusLabel value="active" label="Active" />
            ) : null}
            {status === 'pending' ? (
              <StatusLabel value="pending" label="Pending" />
            ) : null}
          </div>
          <div className="flex gap-4">
            {invitation && user.type === 'invitation' && (
              <ResendOrgInvitation
                invitationId={invitation.id}
                orgId={invitation.organization.id}
              />
            )}
            {user.role !== 'organization_owner' &&
              user.email !== currentUser?.email &&
              !(
                user.role === 'organization_admin' &&
                membership.organization.role === 'organization_admin'
              ) &&
              orgMember?.role !== 'organization_member' && (
                <ConfirmDeleteUserDialog
                  user={user}
                  trigger={{
                    className: 'text-[#666666]',
                  }}
                  level="organization"
                />
              )}
          </div>
        </div>
      );
    },
    header: () => <div>Status</div>,
  }),
];

function getSelectedKeys<T>(filter: { [key: string]: boolean }): T[] {
  return Object.keys(filter).filter(
    (key) => filter[key as keyof typeof filter]
  ) as T[];
}

const UsersTable = () => {
  const [data, setData] = useState<DataRow[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const isOpenInviteDialog = searchParams.get('add') === 'true';

  const [openInviteFormDialog, setOpenInviteFormDialog] =
    useState(isOpenInviteDialog);

  const { data: currentOrg } = useCurrentOrganization();

  const { orgId } = useGetRouteParams();

  const { data: projects } = useGetProjects(
    {
      orgId,
    },
    {
      enabled: !!orgId,
    }
  );

  const form = useForm({
    defaultValues: {
      search: '',
      roles: {},
      projects: {},
      status: 'all',
    },
  });

  const { watch } = form;

  const filterRoles = watch('roles') as Record<OrganizationRole, boolean>;
  const filterProjects = watch('projects') as Record<number, boolean>;
  const filterStatus = watch('status') as UserStatus & 'all';

  const selectedRoles = getSelectedKeys<OrganizationRole>(filterRoles);
  const selectedProjects = getSelectedKeys<number>(filterProjects);

  const {
    data: org_invitations,
    isLoading: isFetchingOrgInvitations,
    isRefetching: isRefetchingOrgInvitations,
  } = useGetOrgInvitations(
    {
      orgId,
      projects: selectedProjects.join(',') || undefined,
      roles: selectedRoles.join(',') || undefined,
    },
    {
      enabled:
        !!orgId && (filterStatus === 'pending' || filterStatus === 'all'),
    }
  );

  const {
    data: org_members,
    isLoading: isFetchingOrgMembers,
    isRefetching: isRefetchingOrgMembers,
  } = useGetOrgMembers(
    {
      orgId,
      projects: selectedProjects.join(',') || undefined,
      roles: selectedRoles.join(',') || undefined,
    },
    {
      enabled: !!orgId && (filterStatus === 'active' || filterStatus === 'all'),
    }
  );

  useEffect(() => {
    const dataRows: DataRow[] = [];

    if (org_invitations && org_invitations?.length > 0) {
      org_invitations.forEach((i) =>
        dataRows.push({
          id: i.id,
          user: {
            id: i.id,
            name: null,
            avatarImage: '',
            email: i.email,
            role: i.role,
          },
          projects: null,
          lastActive: null,
          status: 'pending',
          type: 'invitation',
        })
      );
    }

    if (org_members && org_members?.length > 0) {
      org_members.forEach((m) =>
        dataRows.push({
          id: m.id,
          lastActive: m.date_joined,
          projects: m.project_memberships
            .map((p) => p.project)
            .filter((p) => projects?.map((pr) => pr.id).includes(p.id)),
          user: {
            id: m.user.id,
            name: `${m.user.first_name} ${m.user.last_name}`,
            avatarImage: m.user.profile_pic,
            role: m.role,
            email: m.user.email,
          },
          type: 'member',
          status: 'active',
        })
      );
    }

    setData(dataRows);
  }, [org_invitations, org_members, projects]);

  const statusResults = useMemo(() => {
    if (filterStatus === 'all') {
      return data;
    } else {
      return data.filter((d) => d.status === filterStatus);
    }
  }, [filterStatus, data]);

  const searchText = watch('search');

  const searchResults = useGetSearchResults<DataRow>({
    searchValue: searchText,
    data: statusResults,
    filterFields: ['user.name', 'user.email'],
  });

  const navigate = useNavigate();

  const handleSelect = (id: number) => {
    const record = data.find((d) => d.id === id);

    const type = record?.type;

    if (type === 'invitation') {
      return;
    }

    navigate({
      pathname: `${id}`,
      search: searchParams.toString(),
    });
  };

  const table = createTanstackTable({
    data: searchResults ?? data,
    columns: columns({ handleSelect }),
    defaultSorting: {
      id: 'lastActive',
      desc: true,
    },
  });

  const isAdmin = currentOrg && isOrgAdmin(currentOrg.role);

  const isLoading =
    isFetchingOrgMembers ||
    isFetchingOrgInvitations ||
    isRefetchingOrgMembers ||
    isRefetchingOrgInvitations;

  const handleOpenInviteFormDialog = () => {
    setOpenInviteFormDialog(true);
  };

  const handleCloseInviteFormDialog = () => {
    setOpenInviteFormDialog(false);
    setSearchParams((prev) => {
      prev.delete('add');
      return prev;
    });
  };

  const projectOptions =
    projects?.map((p) => ({
      value: p.id,
      label: p.name,
    })) ?? [];

  return (
    <div className="w-full">
      <FormProvider {...form}>
        <form className="flex flex-col gap-4">
          <div className="flex justify-between">
            <div className="flex flex-wrap grow gap-2">
              <div className="w-full max-w-[350px]">
                <SearchInput />
              </div>
              {projectOptions.length > 0 && (
                <CustomMenuDropdown
                  name="projects"
                  label="Project"
                  data={projectOptions}
                />
              )}
              <CustomMenuDropdown name="roles" label="Role" data={roles} />
              <FormSelect
                name="status"
                fullWidth={false}
                defaultValue="all"
                options={
                  [
                    {
                      value: 'all',
                      label: 'All Status',
                    },
                    {
                      value: 'active',
                      label: 'Active',
                    },
                    {
                      value: 'pending',
                      label: 'Pending',
                    },
                  ] as {
                    value: UserStatus;
                    label: string;
                  }[]
                }
              />
            </div>
            <div className="">
              {isAdmin && (
                <InviteOrgFormDialog
                  roles={roles}
                  open={openInviteFormDialog}
                  handleClose={handleCloseInviteFormDialog}
                  handleOpen={handleOpenInviteFormDialog}
                />
              )}
            </div>
          </div>
          <TanstackTable
            table={table}
            handleSelect={{
              lockColIndex: 2,
              onClick: handleSelect,
            }}
            isLoading={isLoading}
            emptyState={
              searchText
                ? 'No results'
                : 'No users available. Please invite a user.'
            }
          />
        </form>
      </FormProvider>
      <div className="h-4" />
    </div>
  );
};

export default UsersTable;
